import React from 'react';
import { NavLink } from 'react-router-dom';
import { Home, Library, Plus, Settings, Clapperboard } from 'lucide-react';
import SidebarV2 from '../Sidebar/SidebarV2';
import ThemeToggle from '../ui/ThemeToggle';
import { AppView } from '../../../types';
import BrightforgeMark from '../../BrightforgeMark';

interface MobileShellProps {
  currentView: AppView;
  onChangeView: (view: AppView) => void;
  queueCount?: number;
  planName?: string;
  userEmail?: string | null;
  userDisplayName?: string | null;
  onSignOut?: () => Promise<void> | void;
  children: React.ReactNode;
}

export const MobileShell: React.FC<MobileShellProps> = ({
  currentView,
  onChangeView,
  queueCount,
  planName,
  userEmail,
  userDisplayName,
  onSignOut,
  children,
}) => {
  const mobileNav = [
    { view: AppView.HOME, label: 'Home', path: '/dashboard', icon: Home },
    { view: AppView.CREATE, label: 'Create', path: '/create', icon: Plus },
    { view: AppView.EDITOR, label: 'Editor', path: '/editor', icon: Clapperboard },
    { view: AppView.LIBRARY, label: 'Library', path: '/library', icon: Library },
    { view: AppView.SETTINGS, label: 'Settings', path: '/settings', icon: Settings },
  ];

  return (
    <div className="flex h-[100dvh] min-w-0 overflow-hidden bg-[var(--background)] text-[var(--foreground)]">
      <div className="hidden lg:flex shrink-0">
        <SidebarV2
          currentView={currentView}
          onChangeView={onChangeView}
          queueCount={queueCount}
          planName={planName}
          userEmail={userEmail}
          userDisplayName={userDisplayName}
          onSignOut={onSignOut}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-[var(--border-subtle)] bg-[var(--editor-chrome,var(--background))] px-4 lg:px-5">
          <div className="flex items-center gap-2 lg:hidden">
            <BrightforgeMark className="h-7 w-7" tileClassName="bg-[var(--foreground)]" glyphClassName="text-[var(--background)]" />
            <span className="text-sm font-semibold tracking-tight">PIXLIE</span>
          </div>
          {queueCount ? (
            <span className="ml-auto text-[11px] tabular-nums text-[var(--text-muted)] lg:ml-0">
              {queueCount} in queue
            </span>
          ) : null}
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
          </div>
        </header>
        <main className="relative min-w-0 flex-1 overflow-auto pb-[calc(4.75rem+env(safe-area-inset-bottom))] custom-scrollbar animate-fade-in lg:pb-0">
          {children}
        </main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-[var(--border-subtle)] bg-[var(--sidebar)]/95 px-2 pb-[env(safe-area-inset-bottom)] pt-2 backdrop-blur-xl lg:hidden" aria-label="Primary navigation">
        {mobileNav.map(({ view, label, path, icon: Icon }) => (
          <NavLink
            key={path}
            to={path}
            onClick={() => onChangeView(view)}
            className={({ isActive }) => [
              'flex min-h-12 flex-col items-center justify-center gap-1 rounded-lg text-[10px] font-medium transition-colors',
              isActive ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]',
            ].join(' ')}
          >
            <Icon className="h-[18px] w-[18px]" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default MobileShell;
